import { type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { clsx } from "clsx";
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  Award,
  CalendarDays,
  ClipboardList,
  Users,
  BadgeDollarSign,
  Wallet,
  CalendarCheck,
  ListTodo,
  Clock,
  Scissors,
  Building2,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
  Banknote,
  Coins,
  SlidersHorizontal,
  BookOpen,
  BarChart2,
  Timer,
  BadgeCheck,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import type { PageKey } from "../../types/pages";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  page?: PageKey;
}

interface NavGroupDef {
  label: string;
  items: NavItem[];
}

// ── Employee nav — grouped by domain ────────────────────────────────────────
const employeeGroups: NavGroupDef[] = [
  {
    label: "",
    items: [
      { label: "Dashboard",  href: "/employee/dashboard",    icon: <LayoutDashboard className="h-5 w-5" />, page: "employee:dashboard" },
      { label: "Tasks",      href: "/employee/tasks",        icon: <ClipboardList    className="h-5 w-5" />, page: "employee:tasks" },
    ],
  },
  {
    label: "Financials",
    items: [
      { label: "Payslip",          href: "/employee/payslip",      icon: <FileText  className="h-5 w-5" />, page: "employee:payslip" },
      { label: "My Wallet",        href: "/employee/wallet",       icon: <Banknote  className="h-5 w-5" />, page: "employee:wallet" },
      { label: "Cash Advance",     href: "/employee/cash-advance",      icon: <Coins     className="h-5 w-5" />, page: "employee:cash-advance" },
      { label: "Loan Application", href: "/employee/loans",             icon: <CreditCard className="h-5 w-5" />, page: "employee:loans" },
      { label: "Work Time Claims", href: "/employee/work-adjustments",  icon: <Timer     className="h-5 w-5" />, page: "employee:work-adjustments" },
    ],
  },
  {
    label: "Leave & Documents",
    items: [
      { label: "Leave Application",         href: "/employee/leave", icon: <CalendarDays className="h-5 w-5" />, page: "employee:leave" },
      { label: "Certificate of Employment", href: "/employee/coe",   icon: <Award        className="h-5 w-5" />, page: "employee:coe" },
    ],
  },
];

// ── Admin nav — grouped by domain ───────────────────────────────────────────
// Dynamic items (Manage Roles, Manage Businesses) are injected at runtime
// into "People" (idx 1) and "Management" (idx 4) respectively.
const adminBaseGroups: NavGroupDef[] = [
  {
    label: "Overview",
    items: [
      { label: "Admin Dashboard", href: "/admin/dashboard", icon: <LayoutDashboard className="h-5 w-5" />, page: "admin:dashboard" },
    ],
  },
  {
    label: "People",
    items: [
      { label: "Manage Users", href: "/admin/users",      icon: <Users  className="h-5 w-5" />, page: "admin:users" },
      { label: "Attendance",   href: "/admin/attendance", icon: <Clock  className="h-5 w-5" />, page: "admin:attendance" },
    ],
  },
  {
    label: "Payroll",
    items: [
      { label: "Process Payslip",     href: "/admin/payslip",     icon: <BadgeDollarSign  className="h-5 w-5" />, page: "admin:payslip" },
      { label: "Payslip Adjustments", href: "/admin/adjustments", icon: <SlidersHorizontal className="h-5 w-5" />, page: "admin:adjustments" },
    ],
  },
  {
    label: "Approvals",
    items: [
      { label: "Manage Loans",        href: "/admin/loans",        icon: <Wallet      className="h-5 w-5" />, page: "admin:loans" },
      { label: "Manage Leave",        href: "/admin/leave",        icon: <CalendarCheck className="h-5 w-5" />, page: "admin:leave" },
      { label: "Manage Cash Advance",  href: "/admin/cash-advance",      icon: <Coins  className="h-5 w-5" />, page: "admin:cash-advance" },
      { label: "Work Time Claims",     href: "/admin/work-adjustments",  icon: <Timer  className="h-5 w-5" />, page: "admin:work-adjustments" },
    ],
  },
  {
    label: "Management",
    items: [
      { label: "Manage Tasks",        href: "/admin/tasks",   icon: <ListTodo  className="h-5 w-5" />, page: "admin:tasks" },
      { label: "Manage Wallet",       href: "/admin/wallet",  icon: <Wallet    className="h-5 w-5" />, page: "admin:wallet" },
      { label: "Reports",             href: "/admin/reports",     icon: <BarChart2 className="h-5 w-5" />, page: "admin:reports" },
      { label: "Transaction Ledger",  href: "/admin/ledger",      icon: <BookOpen  className="h-5 w-5" />, page: "admin:ledger" },
      { label: "Financial Dashboard", href: "/admin/financials",  icon: <BarChart2 className="h-5 w-5" />, page: "admin:financials" },
    ],
  },
];

// ── NavGroup ─────────────────────────────────────────────────────────────────
function NavGroup({
  label,
  items,
  collapsed,
  onMobileClose,
}: {
  label: string;
  items: NavItem[];
  collapsed: boolean;
  onMobileClose?: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      {/* Expanded: show text label */}
      {label && !collapsed && (
        <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-white/35 select-none">
          {label}
        </p>
      )}
      {/* Collapsed: thin divider in place of text */}
      {label && collapsed && <div className="mx-2 my-2 h-px bg-white/15" />}
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.href}>
            <NavLink
              to={item.href}
              title={collapsed ? item.label : undefined}
              onClick={onMobileClose}
              className={({ isActive }) =>
                clsx(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                  isActive
                    ? "bg-[#2447d9] text-white shadow-sm"
                    : "text-white/70 hover:bg-white/10 hover:text-white",
                  collapsed && "justify-center px-2",
                )
              }
            >
              <span className="shrink-0">{item.icon}</span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── SidebarContent ───────────────────────────────────────────────────────────
function SidebarContent({
  collapsed,
  onToggle,
  onMobileClose,
  isMobile,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
  isMobile?: boolean;
}) {
  const { isAdmin, isActiveBusinessAdmin, hasPageAccess, loading } = useAuth();
  const hasStoreAccess = isAdmin || hasPageAccess("store:manage-store");

  // Filter employee groups by page access (super-admin sees all)
  const visibleEmployeeGroups: NavGroupDef[] = employeeGroups.map((g) => ({
    ...g,
    items: isAdmin
      ? g.items
      : g.items.filter((item) => !item.page || hasPageAccess(item.page)),
  }));

  // Filter admin groups and inject dynamic items
  const visibleAdminGroups: NavGroupDef[] = adminBaseGroups.map((g, idx) => {
    const filtered = g.items.filter((item) => !item.page || hasPageAccess(item.page));
    // idx 1 = "People": inject Manage Roles (super-admin + business admins) and
    // Manage Approvals (super-admin only).
    if (idx === 1 && (isAdmin || isActiveBusinessAdmin)) {
      const injected = [
        ...filtered,
        { label: "Manage Roles", href: "/admin/roles", icon: <ShieldCheck className="h-5 w-5" /> },
      ];
      if (isAdmin) {
        injected.push({ label: "Manage Approvals", href: "/admin/approvals", icon: <BadgeCheck className="h-5 w-5" /> });
      }
      return { ...g, items: injected };
    }
    // idx 4 = "Management": inject Manage Businesses for super-admin only
    if (idx === 4 && isAdmin) {
      return {
        ...g,
        items: [
          ...filtered,
          { label: "Manage Businesses", href: "/admin/businesses", icon: <Building2 className="h-5 w-5" /> },
        ],
      };
    }
    return { ...g, items: filtered };
  });

  const hasAdminItems = visibleAdminGroups.some((g) => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      {/* ── Logo + collapse toggle ── */}
      <div
        className={clsx(
          "flex items-center border-b border-white/10 shrink-0 h-14",
          collapsed ? "flex-col justify-center gap-1.5 px-2" : "justify-between px-4",
        )}
      >
        {/* Brand mark */}
        <div className={clsx("flex items-center gap-2.5", collapsed && "justify-center")}>
          <img src="/mgc_logo.jpeg" alt="MGC" className="h-8 shrink-0 object-contain" />
          {!collapsed && (
            <div>
              <span className="text-sm font-extrabold text-white leading-none">MDM</span>
              <p className="text-[10px] text-white/60 leading-tight">Group of Company</p>
            </div>
          )}
        </div>

        {/* Desktop collapse toggle — inline with logo */}
        {!isMobile && (
          <button
            onClick={onToggle}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white/45 hover:bg-white/10 hover:text-white transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed
              ? <ChevronRight className="h-4 w-4" />
              : <ChevronLeft  className="h-4 w-4" />
            }
          </button>
        )}

        {/* Mobile close */}
        {isMobile && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 hover:bg-white/10"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {loading && (
          <div className="space-y-1 px-1 pt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-9 rounded-lg bg-white/10 animate-pulse"
                style={{ opacity: 1 - i * 0.15 }}
              />
            ))}
          </div>
        )}

        {!loading && (
          <>
            {/* Employee groups */}
            {visibleEmployeeGroups.map((g) => (
              <NavGroup
                key={`emp_${g.label}`}
                label={g.label}
                items={g.items}
                collapsed={collapsed}
                onMobileClose={onMobileClose}
              />
            ))}

            {/* Administration section */}
            {hasAdminItems && (
              <>
                {/* Section separator — text only when expanded; collapsed groups provide their own dividers */}
                {!collapsed && (
                  <div className="mx-2 mt-3 mb-1 h-px bg-white/10" />
                )}
                {!collapsed && (
                  <p className="px-3 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-white/30 select-none">
                    Administration
                  </p>
                )}
                {visibleAdminGroups.map((g) => (
                  <NavGroup
                    key={`adm_${g.label}`}
                    label={g.label}
                    items={g.items}
                    collapsed={collapsed}
                    onMobileClose={onMobileClose}
                  />
                ))}
              </>
            )}

            {/* Store */}
            {hasStoreAccess && (
              <>
                <div className="mx-2 my-2 h-px bg-white/10" />
                <NavGroup
                  label="Store"
                  items={[{ label: "Manage Store", href: "/store", icon: <Scissors className="h-5 w-5" /> }]}
                  collapsed={collapsed}
                  onMobileClose={onMobileClose}
                />
              </>
            )}
          </>
        )}
      </nav>
    </div>
  );
}

// ── Sidebar shell ────────────────────────────────────────────────────────────
export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-[260px] bg-[#0f1f5c] border-r border-[#0a1640] shadow-[0_4px_16px_0_rgb(0_0_0/0.12)]",
          "transform transition-transform duration-300 ease-in-out md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
        aria-label="Mobile navigation"
      >
        <SidebarContent
          collapsed={false}
          onToggle={onToggle}
          onMobileClose={onMobileClose}
          isMobile
        />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={clsx(
          "hidden md:flex flex-col bg-[#0f1f5c] border-r border-[#0a1640] shrink-0",
          "transition-all duration-300 ease-in-out",
          collapsed ? "w-[64px]" : "w-[260px]",
        )}
        aria-label="Main navigation"
      >
        <SidebarContent collapsed={collapsed} onToggle={onToggle} />
      </aside>
    </>
  );
}
